import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMutation, useQuery } from 'hooks/apollo';
import { gql } from '@apollo/client';
import copy from 'copy-to-clipboard';

import globalMessages from 'translations/messages';

import { Button, Form, Tag, Typography } from 'antd';
import { DeleteFilled, EditFilled, LinkOutlined, StopOutlined, UnlockOutlined, UserAddOutlined, UserSwitchOutlined } from '@ant-design/icons';

import Drawer from 'components/Drawer';
import Table from 'components/Table';
import { EDIT_USERS } from 'shared/constants/permissions';

import useAuth from 'hooks/useAuth';
import useCustomer from 'hooks/useCustomer';
import useUser from 'hooks/useUser';
import { UserStatus } from 'shared/services/prisma/types';
import useGlobalContext from 'hooks/useGlobalContext';
import { renderBool } from 'components/Table/renderers';
import PhoneNumber from 'components/PhoneNumber';
import StatusTag from 'components/StatusTag';
import UserForm from './CustomerUserForm';
import messages from './messages';

const QUERY = gql`
  query EditCustomer_GetUsers($id: ID!) {
    customer (id: $id) {
      id
      customerAccess {
        id
        user {
          id
          email
          firstname
          lastname
          phone
          mfa
          lastLogin
          loginsCount
          status
          invitationUrl
        }
        roles {
          id
          name
          visible
        }
        dataSourceAccess {
          id
          dataSource {
            id
            title
          }
          roles {
            id
            name
          }
        }
      }
    }
    customerRoles: roles(type: CUSTOMER) {
      id
      name
      description
      permissions
    }
    dataSourceRoles: roles(type: DATA_SOURCE) {
      id
      name
      description
      permissions
    }
    settings(keys: ["dataSourceAdvancedUserRoleId"]) {
      id
      key
      value
    }
  }
`;

const UPDATE_USER = gql`
  mutation($input: UpdateUserInput!) {
    updateUser(input: $input) {
      id
      firstname
      lastname
      email
      phone
      mfa
      customerAccess {
        id
        roles {
          id
          name
        }
        dataSourceAccess {
          id
          dataSource {
            id
            title
          }
          roles {
            id
            name
            visible
          }
        }
      }
    }
  }
`;

const REMOVE_USER_FROM_CUSTOMER = gql`
  mutation($id: ID!) {
    removeCustomerAccess(id: $id)
  }
`;

const BLOCK_USER = gql`
  mutation($id: ID!) {
    blockUser(id: $id) { id status customerAccess { id dataSourceAccess { id }} }
  }
`;

const UNBLOCK_USER = gql`
  mutation($id: ID!) {
    unblockUser(id: $id) { id status }
  }
`;

const INVITE_USER = gql`
  mutation($input: InviteUserInput!) {
    inviteUser(input: $input)
  }
`;

const { Text, Paragraph } = Typography;

const CustomerUsers = ({ customer }) => {
  const { data, loading, refetch } = useQuery(QUERY, {
    variables: {
      id: customer.id,
    },
  });

  const [removeUserFromCustomer] = useMutation(REMOVE_USER_FROM_CUSTOMER);
  const [blockUser] = useMutation(BLOCK_USER);
  const [unblockUser] = useMutation(UNBLOCK_USER);

  const [inviteUser, { loading: inviting }] = useMutation(INVITE_USER);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm] = Form.useForm();

  const [updateUser, { loading: updating }] = useMutation(UPDATE_USER, { refetchQueries: ['EditCustomer'] });
  const [customerAccessToEdit, setCustomerAccessToEdit] = useState();
  const [updateForm] = Form.useForm();

  const { formatMessage: $t } = useIntl();
  const { user: currentUser, isSuperUser } = useUser();
  const { hasCustomerPermission } = useCustomer();
  const { impersonate } = useAuth();
  const { modal, message } = useGlobalContext();

  const columns = [
    {
      dataIndex: ['user', 'email'],
      title: <FormattedMessage {...messages.email} />,
      sorter: (a, b) => a.user.email.localeCompare(b.user.email),
      defaultSortOrder: 'ascend',
    },
    {
      dataIndex: ['user', 'firstname'],
      title: <FormattedMessage {...messages.firstname} />,
      sorter: (a, b) => a.firstname?.localeCompare(b.firstname),
    },
    {
      dataIndex: ['user', 'lastname'],
      title: <FormattedMessage {...messages.lastname} />,
      sorter: (a, b) => a.lastname?.localeCompare(b.lastname),
    },
    {
      dataIndex: ['user', 'phone'],
      title: <FormattedMessage {...messages.phone} />,
      sorter: (a, b) => a.phone?.localeCompare(b.phone),
      visible: false,
      render: (phone) => <PhoneNumber number={phone} />,
    },
    {
      dataIndex: 'roles',
      title: <FormattedMessage {...messages.authRoles} />,
      render: (roles) => (roles || []).map((role, i) => (
        <Tag key={i} style={{ marginBottom: 8 }}>
          {role.name}
        </Tag>
      )),
    },
    {
      dataIndex: 'dataSourceAccess',
      title: <FormattedMessage {...messages.dataSourceRoles} />,
      render: (dataSourceAccess) => (
        <ul>
          {dataSourceAccess.map((wa) => (
            <li key={wa.id}>
              {wa.dataSource.title}
              {' - '}
              {wa.roles.map(role => <Tag key={role.id}>{role.name}</Tag>)}
            </li>
          ))}
        </ul>
      ),
    },
    {
      dataIndex: ['user', 'loginsCount'],
      title: <FormattedMessage {...messages.loginCount} />,
      type: 'number',
      visible: false,
    },
    {
      dataIndex: ['user', 'lastLogin'],
      title: <FormattedMessage {...messages.lastLogin} />,
      type: 'relativeDate',
    },
    {
      dataIndex: ['user', 'mfa'],
      title: 'MFA',
      type: 'bool',
      render: mfa => renderBool(!!mfa),
    },
    {
      key: 'status',
      dataIndex: ['user', 'status'],
      title: <FormattedMessage {...globalMessages.status} />,
      render: (status) => <StatusTag status={status} />,
      align: 'center',
    },
  ];

  const onCopyLink = (link) => {
    try {
      copy(link, {
        debug: true,
        format: 'text/plain',
        onCopy: () => message.info($t(messages.linkCopied)),
      });
    } catch (error) {
      message.error(
        <>
          <Text>{$t(messages.linkCopyFailed)}</Text>
          <Paragraph copyable={{ tooltips: false }}>{link}</Paragraph>
        </>,
      );
    }
  };

  const onInvite = async ({ email, roleIds, dataSourceAccess }) => {
    try {
      const result = await inviteUser({

        variables: {
          input: {
            email,
            customerAccess: {
              customerId: customer.id,
              roleIds,
              dataSourceAccess,
            },
          },
        },
      });

      if (result.data.inviteUser === UserStatus.Active) {
        message.success($t(messages.alreadyActive, { email, customer: customer.name }));
      } else if (result.data.inviteUser === UserStatus.Invited) {
        message.success($t(messages.invitationSent, { email }));
      } else if (result.data.inviteUser) {
        message.success($t(messages.alreadyAccess, { email, customer: customer.name }));
      } else {
        message.error($t(messages.inviteUserFailed));
      }

      setShowInvite(false);
      refetch();
    } catch (error) {
      // TODO: This doesn't work, the error is somehow caught by ErrorBoundary before it gets here
      console.log(error);
      message.error($t(messages.inviteUserFailed));
    }
  };

  const onUpdate = async ({ firstname, lastname, phone, roleIds, dataSourceAccess }) => {
    try {
      await updateUser({
        variables: {
          input: {
            id: customerAccessToEdit.user.id,
            firstname,
            lastname,
            phone,
            customerAccess: {
              customerId: customer.id,
              roleIds,
              dataSourceAccess,
            },
          },
        },
      });
      setCustomerAccessToEdit(false);
      message.success($t(globalMessages.successfullFormFeedback));
    } catch (error) {
      // TODO: This doesn't work, the error is somehow caught by ErrorBoundary before it gets here
      console.log(error);
      message.error($t(messages.updateUserFailed));
    }
  };

  const onRemove = async (customerAccess) => {
    modal.confirm({
      title: $t(messages.confirmRemoveUser),
      content: $t(messages.confirmRemoveUserDescription, { email: customerAccess.user.email, customer: customer.name }),
      okText: $t(messages.removeFromCustomer),
      okType: 'danger',
      async onOk() {
        await removeUserFromCustomer({
          variables: {
            id: customerAccess.id,
          },
        });

        refetch();
      },
    });
  };

  const onBlock = async (userId) => {
    await blockUser({
      variables: { id: userId },
    });

    // refetch();
  };

  const onUnblock = async (userId) => {
    await unblockUser({
      variables: { id: userId },
    });

    // refetch();
  };

  const canEdit = hasCustomerPermission(EDIT_USERS);

  const actions = canEdit && ((customerAccess) => (
    <Button icon={<EditFilled />} onClick={() => setCustomerAccessToEdit(customerAccess)}>
      <FormattedMessage {...globalMessages.edit} />
    </Button>
  ));

  const actionsMenuItems = canEdit && (({ id, user }) => [{
    key: 'impersonate',
    label: <FormattedMessage {...messages.impersonate} />,
    onClick: () => impersonate(user.id),
    icon: <UserSwitchOutlined />,
    visible: isSuperUser && user.id !== currentUser.id,
  }, {
    key: 'copyInvitation',
    label: <FormattedMessage {...messages.copyInvitationLink} />,
    onClick: () => onCopyLink(user.invitationUrl),
    icon: <LinkOutlined />,
    visible: user.status === UserStatus.Invited,
  }, {
    key: 'unblock',
    label: <FormattedMessage {...messages.unblockUser} />,
    onClick: () => onUnblock(user.id),
    icon: <UnlockOutlined />,
    visible: user.status === UserStatus.Blocked,
  }, {
    key: 'block',
    label: <FormattedMessage {...messages.blockUser} />,
    onClick: () => onBlock(user.id),
    icon: <StopOutlined />,
    visible: user.status !== UserStatus.Blocked && user.id !== currentUser.id,
  }, {
    key: 'remove',
    label: <FormattedMessage {...messages.removeFromCustomer} />,
    onClick: () => onRemove({ id, user }),
    danger: true,
    icon: <DeleteFilled />,
  }]);

  const dataSourceAdvancedUserRoleId = data?.settings.find(s => s.key === 'dataSourceAdvancedUserRoleId')?.value;

  return (
    <>
      <Table
        loading={loading}
        columns={columns}
        dataSource={data?.customer.customerAccess}
        actions={actions}
        actionsMenuItems={actionsMenuItems}
        footerActions={canEdit && (
          <Button type="primary" onClick={() => setShowInvite(true)} icon={<UserAddOutlined />} className="invite-user">
            <FormattedMessage {...messages.inviteUser} />
          </Button>
        )}
      />
      <Drawer
        name="edit-user"
        title={<FormattedMessage {...messages.editUser} />}
        open={!!customerAccessToEdit}
        onClose={() => setCustomerAccessToEdit(false)}
        onCancel={() => setCustomerAccessToEdit(false)}
        onOk={updateForm.submit}
        confirmLoading={updating || updating}
        destroyOnClose
      >
        {customerAccessToEdit && (
        <UserForm
          customerAccess={customerAccessToEdit}
          form={updateForm}
          onSubmit={onUpdate}
          customerRoles={data?.customerRoles}
          dataSourceRoles={data?.dataSourceRoles}
          dataSources={customer.dataSources}
          dataSourceAdvancedUserRoleId={dataSourceAdvancedUserRoleId}
        />
        )}
      </Drawer>
      <Drawer
        name="invite-user"
        title={<FormattedMessage {...messages.inviteUser} />}
        open={showInvite}
        onClose={() => setShowInvite(false)}
        onCancel={() => setShowInvite(false)}
        onOk={inviteForm.submit}
        confirmLoading={inviting}
        okLabel={<FormattedMessage {...messages.sendInvitation} />}
        destroyOnClose
      >
        <UserForm
          form={inviteForm}
          onSubmit={onInvite}
          customerRoles={data?.customerRoles}
          dataSourceRoles={data?.dataSourceRoles}
          dataSources={customer.dataSources}
          dataSourceAdvancedUserRoleId={dataSourceAdvancedUserRoleId}
        />
      </Drawer>
    </>
  );
};

export default CustomerUsers;
